<script setup lang="ts">
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { AlertType, IAlert } from '@/interfaces/IAlert';
  import { vueData } from '@/VueData';

  /**
   * The app's toasts (plan §4.2): dark cards stacked bottom-centre (newest
   * on top, spaced by their real heights), an icon by type and an optional
   * action. `vueData.addAlert` removes each one after
   * its time.
   */
  defineProps<{ alerts: IAlert[] }>();

  const ICONS: Partial<Record<AlertType, string>> = {
    [AlertType.Success]: 'check-circle',
    [AlertType.Error]: 'alert-circle',
    [AlertType.Info]: 'information',
  };

  function runAction(alert: IAlert) {
    vueData.removeAlert(alert);
    alert.action?.cb();
  }
</script>

<template>
  <!-- The app's topmost transient layer: z-index 10000 beats the GPTHelp
       chat widget (position: fixed, z-index 9999 inside its shadow DOM),
       otherwise toasts triggered from the chat (e.g. the export download
       prompt) render hidden behind it. Tooltips (10000, body-appended) still
       paint above through DOM order. -->
  <div class="v2-shell-alerts hidden-print-only" aria-live="polite">
    <div
      v-for="alert in alerts"
      :key="alert.id"
      class="shell-toast"
      :class="`type-${alert.type || 'normal'}`"
      role="status"
    >
      <PosUiIcon
        v-if="ICONS[alert.type]"
        :name="ICONS[alert.type]"
        class="toast-icon"
      />
      <span class="toast-message">{{ alert.message }}</span>
      <button
        v-if="alert.action"
        type="button"
        class="toast-action"
        @click="runAction(alert)"
      >
        {{ alert.action.text }}
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
  // A bottom strip that stacks the toasts upwards; clicks pass through it.
  // It ends at the docked «Asistente» panel (0 px otherwise).
  .v2-shell-alerts {
    @apply tw-pointer-events-none tw-fixed tw-left-0 tw-flex tw-flex-col-reverse tw-items-center tw-px-4;
    right: var(--assistant-inset-right, 0px);
    z-index: 10000;
    // useToastObstacle (update prompt, selection bar) raises the toasts above
    // those elements through --app-toast-bottom.
    bottom: var(--app-toast-bottom, 16px);

    // Phones: full width, 84 px up on every page: above the GPTHelp bubble
    // where it shows and above the fixed bottom bars that are not obstacles
    // (POS tabs and footers, sheet footers).
    @media (max-width: 599px) {
      @apply tw-items-stretch;
      bottom: var(--app-toast-bottom, 84px);
    }

    .shell-toast {
      @apply tw-pointer-events-auto tw-flex tw-min-h-12 tw-w-max tw-items-center tw-rounded-xl tw-bg-neutral-900 tw-px-4 tw-py-2 tw-text-sm tw-text-on-inverse tw-shadow-lg;
      max-width: 560px;

      @media (max-width: 599px) {
        @apply tw-w-auto;
        max-width: none;
      }

      // Margin, not flex gap: old POS hardware (Chrome < 84).
      & + .shell-toast {
        @apply tw-mb-2;
      }
    }

    .toast-icon {
      @apply tw-mr-3 tw-shrink-0 tw-text-xl;
    }

    .type-success .toast-icon {
      @apply tw-text-emerald-400;
    }

    .type-error .toast-icon {
      @apply tw-text-red-400;
    }

    .type-info .toast-icon {
      @apply tw-text-sky-400;
    }

    .toast-message {
      @apply tw-min-w-0 tw-flex-1;
    }

    .toast-action {
      @apply tw-ml-3 tw-h-11 tw-shrink-0 tw-cursor-pointer tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-3 tw-text-sm tw-font-semibold tw-text-on-inverse;

      &:hover {
        @apply tw-bg-on-inverse/10;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }
</style>
